import { Link, useLocation } from "react-router-dom";
import { paths } from "src/routes/paths";
import Button from "src/components/ui/button";
import { useLocales } from "src/locales";
import { useEffect, useState } from "react";
import { useMediaQuery } from "@mui/system";
import { useRouter } from "src/routes/hooks";
import { Menu, X } from "lucide-react";
import { Dialog } from "@headlessui/react";

const HEADER_LINKS = ["Contact Us", "About Us"];

const BIZ_LOGIN = "https://travelandz.business/business-travel/auth/login";
const BIZ_SIGN_UP = "https://travelandz.business/business-travel/auth/register";
const HOME_2_PATH = "/home2";

const baseBlackColors = {
    textColor: "black",
    navbarcolors: "black",
    logoUrl: "/assets/LOGO_NEGRO.webp",
};

const baseWhiteColors = {
    textColor: "white",
    navbarcolors: "white",
    logoUrl: "/assets/LOGOTIPO_BLANCO_TOTAL.png",
};

const colorsInsersection = {
    home_hero: {
        ...baseWhiteColors,
        pre_header_item: {
            transform: "translateY(0%)",
        },
    },
    trust_companies: {
        ...baseBlackColors,
        pre_header_item: {
            transform: "translateY(-200%)",
        },
    },
    home_dark_mode_section: {
        ...baseBlackColors,
        transform: "translateY(-200%)",
    },
    home_3_benefits: {
        ...baseBlackColors,
        transform: "translateY(-200%)",
    },
    home_3_testimonials: {
        ...baseBlackColors,
        transform: "translateY(-200%)",
    },
    blue_banner: {
        ...baseWhiteColors,
        transform: "translateY(-200%)",
    },
    footer: {
        ...baseBlackColors,
        transform: "translateY(-200%)",
    },
};

const Header = ({ activeSection }) => {
    const { pathname } = useLocation();
    const { t } = useLocales();

    const colors = colorsInsersection[activeSection] || baseBlackColors;

    const [isOpen, setIsOpen] = useState(false);

    const isMobile = useMediaQuery("(max-width: 768px)");

    const [menuOpen, setMenuOpen] = useState(false);

    const router = useRouter();

    const toggleMenu = () => setMenuOpen((prev) => !prev);

    const navLinks = [
        { label: t("Home"), to: "/" },
        { label: t("Contact Us"), to: "/request-a-demo" },
        { label: t("About Us"), to: "/about-us" },
        { label: t("Travel Agencies"), to: "/home2" },
        {
            label: t("Login for Travel Agencies"),
            to: "/travel-agencies/modern/login",
        },
    ];

    useEffect(() => {
        if (!menuOpen) return;

        if (!isMobile) {
            setMenuOpen(false);
        }
    }, [isMobile]);

    return (
        <>
            <div className="fixed top-0 left-0 w-full bg-transparent border-gray-200 z-[999] header-animation">
                <div
                    className="w-full text-sm text-center px-4 py-1 font-bold absolute transition-all text-black duration-300"
                    style={{
                        transform: "translateY(-200%)",
                        ...(colors.pre_header_item && {
                            ...colors.pre_header_item,
                        }),
                        backgroundColor: "#f9fafb",
                    }}
                >
                    {t("Book your Business Travel Now. Pay Later.")}{" "}
                    <button
                        onClick={() => setIsOpen(true)}
                        className="underline font-semibold ml-2 text-black hover:text-blue-600"
                    >
                        {t("Find out More")}
                    </button>
                </div>
                {/* Modal Dialog */}
                <Dialog
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    className="relative z-[9999]"
                >
                    <div
                        className="fixed inset-0 bg-black/40"
                        aria-hidden="true"
                    />

                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Dialog.Panel className="max-w-lg w-full rounded-xl bg-white p-6 shadow-xl space-y-4">
                            <div className="flex justify-between items-start">
                                <Dialog.Title className="text-lg font-bold text-gray-800">
                                    {t(
                                        "Book Now, Pay Later — Built for Growing Businesses",
                                    )}
                                </Dialog.Title>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X />
                                </button>
                            </div>

                            <Dialog.Description className="text-gray-600 text-sm">
                                {t(
                                    "With Travelandz, you can book the business travel you need today — and pay at the end of the month. Our financing solution is designed to help your company stay liquid, move faster, and grow smarter.",
                                )}
                            </Dialog.Description>

                            <ul className="list-disc pl-5 text-gray-700 text-sm space-y-2">
                                <li>
                                    {t(
                                        "Book flights and hotels as needed — without upfront costs.",
                                    )}
                                </li>
                                <li>
                                    {t(
                                        "Receive a consolidated invoice at the end of the month.",
                                    )}
                                </li>
                                <li>
                                    {t(
                                        "Benefit from 30-day payment terms to protect your cash flow.",
                                    )}
                                </li>
                                <li>
                                    {t(
                                        "Stay focused on what matters: growing your business.",
                                    )}
                                </li>
                                <li>
                                    {t(
                                        "Flexible financial tools: confirming, factoring, credit lines.",
                                    )}
                                </li>
                                <li>
                                    {t(
                                        "Tailored risk scoring and coverage for your company profile.",
                                    )}
                                </li>
                                <li>
                                    {t(
                                        "Supported by trusted fintech and insurance partners.",
                                    )}
                                </li>
                            </ul>

                            <div className="pt-4 text-right">
                                <a
                                    href="/pay-later"
                                    className="inline-block bg-primary text-white px-4 py-2 rounded hover:bg-opacity-90 transition"
                                >
                                    {t("Learn More")}
                                </a>
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>

                <div
                    className="w-full flex flex-col py-4 md:px-8"
                    style={{
                        ...(activeSection === "home_hero"
                            ? { paddingTop: isMobile ? "3.5rem" : "2rem" }
                            : {}),
                    }}
                >
                    <div
                        id="navbar_home_page"
                        className="container mx-auto flex px-4 items-center justify-between text-dark"
                    >
                        {/* Logo */}
                        <img
                            src={colors.logoUrl}
                            alt="Logo"
                            onClick={() => router.push("/")}
                            className="h-8 md:h-10 cursor-pointer"
                        />
                        <nav className="hidden">
                            {HEADER_LINKS.map((item) => (
                                <Link
                                    key={item}
                                    to={`/${item
                                        .toLowerCase()
                                        .replace(" ", "-")}`}
                                    className="text-lg font-medium text-gray-900 hover:text-blue-600 transition"
                                    style={{ color: colors.textColor }}
                                >
                                    {t(item)}
                                </Link>
                            ))}
                            <Link
                                to="/home2"
                                className="text-lg font-medium text-gray-900 hover:text-blue-600 transition"
                                style={{
                                    color: colors.textColor,
                                }}
                            >
                                {t("Travel Agencies")}
                            </Link>
                        </nav>
                        {/* Right-side controls */}
                        <div className="flex items-center space-x-4">
                            {/* BUTTONS here */}
                            <div className="flex space-x-4">
                                <Button
                                    variant="outline"
                                    className="hidden md:block"
                                    href={
                                        pathname === HOME_2_PATH
                                            ? paths.authDemo.modern.login
                                            : BIZ_LOGIN
                                    }
                                    style={{
                                        color: colors.textColor,
                                    }}
                                >
                                    {t("Login")}
                                </Button>

                                <Button
                                    variant="default"
                                    className="hidden md:block" // This hides the button on mobile and shows it on md and larger screens
                                    href={
                                        pathname === HOME_2_PATH
                                            ? paths.authDemo.modern.register
                                            : BIZ_SIGN_UP
                                    }
                                >
                                    {t("Try it for Free")}
                                </Button>
                            </div>

                            {/* Hamburger (always visible) */}
                            <button
                                onClick={toggleMenu}
                                style={{ zIndex: 50, color: colors.textColor }}
                            >
                                {menuOpen ? (
                                    <X size={24} />
                                ) : (
                                    <Menu size={24} />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {menuOpen && (
                <div className="fixed inset-0 z-[500] h-screen bg-white/95 flex flex-col items-center justify-center pt-24 space-y-6 text-xl font-semibold overflow-y-auto">
                    {navLinks.map((link, i) => (
                        <Link
                            key={i}
                            to={link.to}
                            onClick={() => setMenuOpen(false)}
                            className="hover:text-blue-600 transition"
                            style={{
                                color: "black",
                            }}
                        >
                            {link.label}
                        </Link>
                    ))}

                    <Button
                        variant="default"
                        href={
                            pathname === HOME_2_PATH
                                ? paths.authDemo.modern.register
                                : BIZ_SIGN_UP
                        }
                        className="w-40"
                        onClick={() => setMenuOpen(false)}
                    >
                        {t("Start Now ")}
                    </Button>
                    <Button
                        variant="secondary"
                        href={
                            pathname === HOME_2_PATH
                                ? paths.authDemo.modern.login
                                : BIZ_LOGIN
                        }
                        className="w-40 hover:text-white"
                        onClick={() => setMenuOpen(false)}
                    >
                        {t("Login")}
                    </Button>
                </div>
            )}
        </>
    );
};

export default Header;
